import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminLandingApi,
  adminFidelityApi,
  adminImageSearchConfigApi,
  adminSiteModeApi,
  adminChatbotApi,
  adminChatbotTracesApi,
  adminPartnerApi,
  adminSchemaRegistryApi,
} from './api';

// ── Query key factories ──────────────────────────────────────────────

export const adminLandingKeys = {
  all: ['admin-landing'],
  settings: () => ['admin-landing', 'settings'],
  public: () => ['admin-landing', 'public'],
  hero: () => ['admin-landing', 'hero'],
  cards: () => ['admin-landing', 'cards'],
};

export const adminFidelityKeys = {
  all: ['admin-fidelity'],
  tiers: () => ['admin-fidelity', 'tiers'],
};

export const adminImageSearchConfigKeys = {
  all: ['admin-image-search-config'],
};

export const adminSiteModeKeys = {
  all: ['admin-site-mode'],
  history: () => ['admin-site-mode', 'history'],
};

export const adminChatbotKeys = {
  all: ['admin-chatbot'],
  stats: (range: any) => ['admin-chatbot', 'stats', range],
  conversations: (range?: any) => ['admin-chatbot', 'conversations', range],
  models: () => ['admin-chatbot', 'models'],
  history: (sessionId: any) => ['admin-chatbot', 'history', sessionId],
};

export const adminChatbotTracesKeys = {
  all: ['admin-chatbot-traces'],
  sessions: (params: any) => ['admin-chatbot-traces', 'sessions', params],
  stats: (days: any) => ['admin-chatbot-traces', 'stats', days],
  session: (sessionId: any) => ['admin-chatbot-traces', 'session', sessionId],
};

export const adminPartnerKeys = {
  all: ['admin-partners'],
  list: (params: any) => ['admin-partners', 'list', params],
  detail: (id: any) => ['admin-partners', 'detail', id],
};

export const adminSchemaRegistryKeys = {
  all: ['admin-schema-registry'],
  list: (params: any) => ['admin-schema-registry', 'list', params],
  detail: (id: any) => ['admin-schema-registry', 'detail', id],
  stats: () => ['admin-schema-registry', 'stats'],
};

// =============================================
// Landing hooks
// =============================================

/** Fetch public landing page data */
export function useAdminLandingPublic(options: any) {
  return useQuery({
    queryKey: adminLandingKeys.public(),
    queryFn: () => adminLandingApi.getPublic(),
    ...options,
  });
}

/** Fetch landing settings */
export function useAdminLandingSettings(options: any) {
  return useQuery({
    queryKey: adminLandingKeys.settings(),
    queryFn: () => adminLandingApi.getSettings(),
    ...options,
  });
}

/** Update landing settings */
export function useUpdateLandingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminLandingApi.updateSettings(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.all });
    },
  });
}

/** Seed default landing data */
export function useSeedLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminLandingApi.seed(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.all });
    },
  });
}

/** Fetch hero media list */
export function useAdminHeroMedia(options: any) {
  return useQuery({
    queryKey: adminLandingKeys.hero(),
    queryFn: () => adminLandingApi.getHeroMedia(),
    ...options,
  });
}

/** Add hero media entry */
export function useAddHeroMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminLandingApi.addHeroMedia(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.hero() });
    },
  });
}

/** Upload hero media file */
export function useUploadHeroMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => adminLandingApi.uploadHeroMedia(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.hero() });
    },
  });
}

/** Update a hero media entry */
export function useUpdateHeroMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, data }: any) => adminLandingApi.updateHeroMedia(imageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.hero() });
    },
  });
}

/** Remove a hero media entry */
export function useRemoveHeroMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageId: any) => adminLandingApi.removeHeroMedia(imageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.hero() });
    },
  });
}

/** Fetch landing cards */
export function useAdminLandingCards(options: any) {
  return useQuery({
    queryKey: adminLandingKeys.cards(),
    queryFn: () => adminLandingApi.getCards(),
    ...options,
  });
}

/** Create a landing card */
export function useCreateLandingCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminLandingApi.createCard(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.cards() });
    },
  });
}

/** Update a landing card */
export function useUpdateLandingCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, data }: any) => adminLandingApi.updateCard(cardId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.cards() });
    },
  });
}

/** Remove a landing card */
export function useRemoveLandingCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: any) => adminLandingApi.removeCard(cardId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.cards() });
    },
  });
}

/** Upload a card image */
export function useUploadCardImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => adminLandingApi.uploadCardImage(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLandingKeys.cards() });
    },
  });
}

// =============================================
// Fidelity hooks
// =============================================

/** Fetch fidelity tiers */
export function useAdminFidelityTiers(options: any) {
  return useQuery({
    queryKey: adminFidelityKeys.tiers(),
    queryFn: () => adminFidelityApi.getTiers(),
    ...options,
  });
}

/** Update fidelity tiers */
export function useUpdateFidelityTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tiers: any) => adminFidelityApi.updateTiers(tiers),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminFidelityKeys.all });
    },
  });
}

// =============================================
// Image Search Config hooks
// =============================================

/** Fetch image search config */
export function useAdminImageSearchConfig(options: any) {
  return useQuery({
    queryKey: adminImageSearchConfigKeys.all,
    queryFn: () => adminImageSearchConfigApi.get(),
    ...options,
  });
}

/** Update image search config */
export function useUpdateImageSearchConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: any) => adminImageSearchConfigApi.update(config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminImageSearchConfigKeys.all });
    },
  });
}

/** Reset image search config to defaults */
export function useResetImageSearchConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminImageSearchConfigApi.reset(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminImageSearchConfigKeys.all });
    },
  });
}

// =============================================
// Site Mode hooks
// =============================================

/** Fetch current site mode */
export function useAdminSiteMode(options: any) {
  return useQuery({
    queryKey: adminSiteModeKeys.all,
    queryFn: () => adminSiteModeApi.get(),
    ...options,
  });
}

/** Update site mode */
export function useUpdateSiteMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminSiteModeApi.update(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSiteModeKeys.all });
    },
  });
}

/** Fetch site mode change history */
export function useAdminSiteModeHistory(options: any) {
  return useQuery({
    queryKey: adminSiteModeKeys.history(),
    queryFn: () => adminSiteModeApi.getHistory(),
    ...options,
  });
}

// =============================================
// Chatbot hooks
// =============================================

/** Fetch chatbot stats for a given range */
export function useAdminChatbotStats(range: any, options: any) {
  return useQuery({
    queryKey: adminChatbotKeys.stats(range),
    queryFn: () => adminChatbotApi.getStats(range),
    ...options,
  });
}

/** Fetch chatbot conversations for a given range */
export function useAdminChatbotConversations(range: any, options: any) {
  return useQuery({
    queryKey: adminChatbotKeys.conversations(range),
    queryFn: () => adminChatbotApi.getConversations(range),
    ...options,
  });
}

/** Fetch available AI models */
export function useAdminChatbotModels(options: any) {
  return useQuery({
    queryKey: adminChatbotKeys.models(),
    queryFn: () => adminChatbotApi.getModels(),
    ...options,
  });
}

/** Set the active AI model */
export function useSetChatbotModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modelId: any) => adminChatbotApi.setModel(modelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminChatbotKeys.models() });
    },
  });
}

/** Fetch conversation history for a session */
export function useAdminChatbotHistory(sessionId: any, options: any) {
  return useQuery({
    queryKey: adminChatbotKeys.history(sessionId),
    queryFn: () => adminChatbotApi.getHistory(sessionId),
    enabled: !!sessionId,
    ...options,
  });
}

/** Delete a conversation history */
export function useRemoveChatbotHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: any) => adminChatbotApi.removeHistory(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminChatbotKeys.conversations() });
    },
  });
}

// =============================================
// Chatbot Traces hooks
// =============================================

/** Fetch trace sessions list */
export function useAdminChatbotTraceSessions(params: any, options: any) {
  return useQuery({
    queryKey: adminChatbotTracesKeys.sessions(params),
    queryFn: () => adminChatbotTracesApi.getSessions(params),
    ...options,
  });
}

/** Fetch trace stats */
export function useAdminChatbotTraceStats(days: any, options: any) {
  return useQuery({
    queryKey: adminChatbotTracesKeys.stats(days),
    queryFn: () => adminChatbotTracesApi.getStats(days),
    ...options,
  });
}

/** Fetch a single trace session detail */
export function useAdminChatbotTraceSession(sessionId: any, options: any) {
  return useQuery({
    queryKey: adminChatbotTracesKeys.session(sessionId),
    queryFn: () => adminChatbotTracesApi.getSession(sessionId),
    enabled: !!sessionId,
    ...options,
  });
}

/** Replay a chatbot trace session */
export function useReplayChatbotTrace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminChatbotTracesApi.replay(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminChatbotTracesKeys.all });
    },
  });
}

// =============================================
// Partner hooks
// =============================================

/** Fetch partners list */
export function useAdminPartners(params: any, options: any) {
  return useQuery({
    queryKey: adminPartnerKeys.list(params),
    queryFn: () => adminPartnerApi.getAll(params),
    ...options,
  });
}

/** Fetch a single partner by ID */
export function useAdminPartner(id: any, options: any) {
  return useQuery({
    queryKey: adminPartnerKeys.detail(id),
    queryFn: () => adminPartnerApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

/** Create a new partner */
export function useCreatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => adminPartnerApi.create(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

/** Update an existing partner */
export function useUpdatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }: any) => adminPartnerApi.update(id, formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

/** Update partner status */
export function useUpdatePartnerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: any) => adminPartnerApi.updateStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

/** Soft delete a partner */
export function useRemovePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminPartnerApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

/** Restore a soft-deleted partner */
export function useRestorePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminPartnerApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

/** Permanently delete a partner */
export function useRemovePartnerPermanently() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminPartnerApi.removePermanently(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPartnerKeys.all });
    },
  });
}

// =============================================
// Schema Registry hooks
// =============================================

/** Fetch schemas list */
export function useAdminSchemas(params: any, options: any) {
  return useQuery({
    queryKey: adminSchemaRegistryKeys.list(params),
    queryFn: () => adminSchemaRegistryApi.getAll(params),
    ...options,
  });
}

/** Fetch a single schema by ID */
export function useAdminSchema(id: any, options: any) {
  return useQuery({
    queryKey: adminSchemaRegistryKeys.detail(id),
    queryFn: () => adminSchemaRegistryApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

/** Create a new schema */
export function useCreateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => adminSchemaRegistryApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Update an existing schema */
export function useUpdateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: any) => adminSchemaRegistryApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Delete a schema */
export function useRemoveSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminSchemaRegistryApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Activate a schema */
export function useActivateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminSchemaRegistryApi.activate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Deactivate a schema */
export function useDeactivateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminSchemaRegistryApi.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Duplicate a schema */
export function useDuplicateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: any) => adminSchemaRegistryApi.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminSchemaRegistryKeys.all });
    },
  });
}

/** Fetch schema registry stats */
export function useAdminSchemaStats(options: any) {
  return useQuery({
    queryKey: adminSchemaRegistryKeys.stats(),
    queryFn: () => adminSchemaRegistryApi.getStats(),
    ...options,
  });
}
